import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  type DraggableProvidedDragHandleProps,
} from "@hello-pangea/dnd";
import { EmailComposerDialog } from "./EmailComposerDialog";
import { ProspectOriginCard } from "./prospects/ProspectOriginCard";
import { WhatsAppTemplateDialog } from "./WhatsAppTemplateDialog";
import { ClientProjectDialog } from "./ClientProjectDialog";
import { ProjectWorkspace } from "./projects/ProjectWorkspace";
import { ContractWorkspace } from "./contracts/ContractWorkspace";
import { BudgetSignatureTimeline } from "./BudgetSignatureTimeline";
import { BudgetSignatureDialog } from "./BudgetSignatureDialog";
import { BudgetPaymentPlan } from "./BudgetPaymentPlan";
import { BudgetSubscriptionCard } from "./BudgetSubscriptionCard";
import { ProposalDocumentWorkspace } from "./budget/ProposalDocumentWorkspace";
import { isProposalDocumentReady } from "@/lib/proposalDocument";
import { LeadFlowStepper } from "./leads/LeadFlowStepper";
import { MarkLostDialog } from "./leads/MarkLostDialog";
import { TestimonialRequestCard } from "./leads/TestimonialRequestCard";
import { calculatePackageTotals } from "@/lib/budgetCalc";
import { buildProposalText, estimateBusinessDays } from "@/lib/proposalText";
import { budgetLineSummary, itemQty, itemUnit } from "@/lib/budgetSummary";
import { BudgetItemsEditor } from "./budget/BudgetItemsEditor";
import { itemFromServiceType, blankItem } from "@/lib/budgetItems";
import { computeLeadFlow, pickPaymentsBudget, type FlowAction } from "@/lib/leadFlow";
import { barterBadgeLabel, barterSplit, recurringBilling } from "@/lib/barter";
import { deriveStage, STAGE_META } from "@/lib/pipelineStage";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  ArrowLeft,
  MessageSquare,
  Mail,
  Phone,
  FileText,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  MoreVertical,
  ArrowRight,
  Send,
  Copy,
  GripVertical,
  Lock,
  CheckCircle2,
  ChevronRight,
  X,
  LayoutGrid,
  XCircle,
  RotateCcw,
  PackageCheck,
  Files,
  Handshake,
} from "lucide-react";
import {
  useLead,
  useLeadInteractions,
  useUpdateLead,
  useDeleteLead,
  useAddInteraction,
  type InteractionType,
} from "@/hooks/useLeads";
import { EditLeadDialog } from "./EditLeadDialog";
import { useClientDrawer } from "./ClientDrawer";
import {
  useClientProjects,
  useCreateClientProject,
  useDeleteClientProject,
  useMoveClientProject,
  type ClientProject,
  type ProjectPhase,
} from "@/hooks/useClientProjects";
import {
  useBudgets,
  useCreateBudget,
  useUpdateBudget,
  useDeleteBudget,
  useServiceTypes,
  type Budget,
  type BudgetItemInput,
  type CreateBudgetData,
} from "@/hooks/useBudgets";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { useContracts } from "@/hooks/useContracts";
import { usePaymentPlanByBudget } from "@/hooks/usePayments";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  BUDGET_STATUS_META,
  PHASE_META,
  PRIORITY_TONE,
  TONE_BG_CLASS,
  TONE_TEXT_CLASS,
  projectBudgetMeta,
  statusMeta,
} from "@/lib/statusTone";
import { formatBRL } from "@/lib/format";
import { leadSourceLabel } from "@/lib/leadSource";

// ─── constants ───────────────────────────────────────────────────────────────

const interactionIcons: Record<InteractionType, React.ReactNode> = {
  whatsapp: <MessageSquare className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  call: <Phone className="h-4 w-4" />,
  note: <FileText className="h-4 w-4" />,
};

const interactionLabels: Record<InteractionType, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  call: "Ligação",
  note: "Nota",
};

const interactionDot: Record<InteractionType, string> = {
  whatsapp: "bg-ok",
  email: "bg-primary",
  call: "bg-warn",
  note: "bg-border",
};

const projectTypeLabels: Record<string, string> = {
  diagnostico: "Diagnóstico gratuito",
  automacao_crm: "Automação · CRM",
  automacao_atendimento: "Automação · Atendimento",
  automacao_erp: "Automação · ERP",
};

function projectTypeLabel(value: string | null | undefined) {
  if (!value) return "—";
  if (projectTypeLabels[value]) return projectTypeLabels[value];
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const phases: ProjectPhase[] = ["backlog", "in_progress", "review", "done"];

const SIGNED_BUDGET = new Set(["signed", "accepted"]);
/** Once a proposal is in the client's hands its numbers are frozen — edits go through "Nova versão". */
const LOCKED_BUDGET = new Set(["sent", "partially_signed", "signed", "accepted"]);

const fmt = (v: number) => formatBRL(v);

const HEADER_BTN = "h-9 px-3.5 rounded-[9px] text-[12.5px] font-semibold";
const SECTION = "bg-card border border-border rounded-2xl shadow-card";
const SECTION_TITLE = "text-[15px] font-bold font-display";
const SEG_ITEM =
  "rounded-lg px-4 py-2 text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

// ─── Tab config ──────────────────────────────────────────────────────────────
// Four free-navigation tabs in the order the work actually happens, one
// responsibility each: Proposta (pricing + document + its signature) →
// Contrato (legal document + its signature) → Pagamentos (plan + collection)
// → Entrega. The LeadFlowStepper above them carries the single "next action";
// content-level gates (LockedStep / collectLocked) keep it impossible to e.g.
// record a payment against an unsigned deal.

type TabKey = "proposta" | "contrato" | "pagamentos" | "entrega";
const TAB_KEYS: TabKey[] = ["proposta", "contrato", "pagamentos", "entrega"];

const TAB_LABELS: Record<TabKey, string> = {
  proposta: "Proposta",
  contrato: "Contrato",
  pagamentos: "Pagamentos",
  entrega: "Entrega",
};

// Old bookmarks/links may still carry previous ?tab= values — map them so no
// existing deep link breaks.
const TAB_ALIASES: Record<string, TabKey> = {
  overview: "proposta",
  projects: "entrega",
  sprint: "entrega",
  budget: "proposta",
  signature: "contrato",
  payments: "pagamentos",
};

function resolveTab(param: string | null): TabKey {
  if (param && (TAB_KEYS as string[]).includes(param)) return param as TabKey;
  if (param && param in TAB_ALIASES) return TAB_ALIASES[param];
  return "proposta";
}

// ─── main component ───────────────────────────────────────────────────────────

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // dialogs / UI
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectDialogPhase, setProjectDialogPhase] = useState<ProjectPhase>("backlog");
  const [editingProject, setEditingProject] = useState<ClientProject | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<ClientProject | null>(null);
  const [workspaceProject, setWorkspaceProject] = useState<ClientProject | null>(null);
  const [signatureBudget, setSignatureBudget] = useState<Budget | null>(null);
  const [activeBudgetId, setActiveBudgetId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>(() => resolveTab(searchParams.get("tab")));
  const [editLeadOpen, setEditLeadOpen] = useState(false);
  const [markLostOpen, setMarkLostOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const { openClient } = useClientDrawer();

  // note
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState<InteractionType>("note");
  const [registerOpen, setRegisterOpen] = useState(false);
  const noteTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const ownerName =
    (user?.user_metadata?.name as string | undefined)?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "—";

  // tags
  const [newTag, setNewTag] = useState("");

  // "Próximo passo" — its own free-text column, `leads.next_step` (added in
  // 20260831090000_lead_next_step.sql). Deliberately NOT `leads.notes`
  // (EditLeadDialog's separate "Notas internas" field). Draft state so typing
  // doesn't fire a mutation on every keystroke; saved on blur.
  const [nextStepDraft, setNextStepDraft] = useState("");

  // data
  const { data: lead, isLoading: leadLoading } = useLead(id);
  const { data: interactions, isLoading: interactionsLoading } = useLeadInteractions(id);
  const { data: allProjects = [] } = useClientProjects();
  const { data: leadBudgets = [], isLoading: budgetsLoading } = useBudgets({ lead_id: id });
  const { data: leadContracts = [] } = useContracts(id);

  // mutations
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const addInteraction = useAddInteraction();
  const deleteProject = useDeleteClientProject();
  const moveProject = useMoveClientProject();

  useEffect(() => {
    setNextStepDraft(lead?.next_step ?? "");
  }, [lead?.id, lead?.next_step]);

  const linkedProjects = allProjects.filter((p) => p.lead_id === id);

  const projectsByPhase = phases.reduce(
    (acc, phase) => {
      acc[phase] = linkedProjects.filter((p) => p.phase === phase);
      return acc;
    },
    {} as Record<ProjectPhase, ClientProject[]>,
  );

  // budget status per project
  const projectBudgetStatus = (leadBudgets as Budget[]).reduce(
    (acc, b) => {
      if (!b.client_project_id) return acc;
      const priority = ["rejected", "draft", "sent", "partially_signed", "signed", "accepted"];
      const current = acc[b.client_project_id];
      if (!current || priority.indexOf(b.status) > priority.indexOf(current))
        acc[b.client_project_id] = b.status;
      return acc;
    },
    {} as Record<string, string>,
  );

  // active budget
  const activeBudget = useMemo(() => {
    const list = leadBudgets as Budget[];
    if (activeBudgetId) return list.find((b) => b.id === activeBudgetId) ?? null;
    // default: latest non-rejected
    return list.find((b) => b.status !== "rejected") ?? list[0] ?? null;
  }, [leadBudgets, activeBudgetId]);

  // ── flow state ───────────────────────────────────────────────────────────
  const hasBudget = (leadBudgets as Budget[]).length > 0;
  const hasSignedBudget = (leadBudgets as Budget[]).some((b) => SIGNED_BUDGET.has(b.status));
  const hasSignedContract = leadContracts.some((c) => c.status === "signed");
  // OR-fallback: leads closed via the budget-signature flow alone (before
  // contract signing existed) stay unlocked — no backfill needed.
  const paymentsLocked = !(hasSignedContract || hasSignedBudget);
  const paymentsBudget = useMemo(
    () => pickPaymentsBudget(leadBudgets as Budget[], leadContracts),
    [leadBudgets, leadContracts],
  );
  // The contract is linked to the closed proposal when there is one, so the
  // signed contract and the payment plan always reference the same numbers.
  const contractBudget = paymentsBudget ?? activeBudget;
  // Permuta: only the cash part of the closed proposal is ever billed.
  const paymentsSplit = paymentsBudget
    ? barterSplit(paymentsBudget.net_total, paymentsBudget.barter_percent ?? 0)
    : null;

  const { data: paymentsPlan } = usePaymentPlanByBudget(paymentsBudget?.id);
  // Pagamentos tab: the plan can be defined on the draft before sending (it
  // shows on /sign/budget); collection is gated separately by paymentsLocked.
  const planBudget = contractBudget;
  const planSplit = barterSplit(planBudget?.net_total ?? 0, planBudget?.barter_percent ?? 0);
  const { data: planData } = usePaymentPlanByBudget(planBudget?.id);

  const flow = useMemo(
    () =>
      computeLeadFlow({
        leadStatus: lead?.status ?? "new",
        budgets: leadBudgets as Budget[],
        contracts: leadContracts,
        installments: paymentsPlan?.installments ?? [],
        hasPaymentPlan: !!paymentsPlan?.plan,
        barterOnly: !!paymentsSplit?.isFull,
      }),
    [lead?.status, leadBudgets, leadContracts, paymentsPlan, paymentsSplit?.isFull],
  );

  const stage = lead ? deriveStage(lead, leadBudgets as Budget[], leadContracts) : "new";
  const stageMeta = STAGE_META[stage];
  const isLost = lead?.status === "lost";
  const isDelivered = lead?.status === "delivered";

  // ── handlers ─────────────────────────────────────────────────────────────

  const handleAddNote = () => {
    if (!id || !newNote.trim()) return;
    addInteraction.mutate(
      { leadId: id, type: noteType, content: newNote.trim() },
      {
        onSuccess: () => setNewNote(""),
      },
    );
  };

  const handleQuickInteraction = (type: InteractionType) => {
    setNoteType(type);
    setRegisterOpen(true);
    setTimeout(() => noteTextareaRef.current?.focus(), 0);
  };

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (!id || !tag || lead?.tags?.includes(tag)) {
      setNewTag("");
      return;
    }
    updateLead.mutate({ id, data: { tags: [...(lead?.tags ?? []), tag] } });
    setNewTag("");
  };

  const handleRemoveTag = (tag: string) => {
    if (!id) return;
    updateLead.mutate({ id, data: { tags: (lead?.tags ?? []).filter((t) => t !== tag) } });
  };

  const handleFollowUpChange = (value: string) => {
    if (!id) return;
    updateLead.mutate({ id, data: { next_follow_up_at: value || null } });
  };

  const handleNextStepBlur = () => {
    if (!id) return;
    const v = nextStepDraft.trim();
    if (v !== (lead?.next_step ?? "").trim()) {
      updateLead.mutate({ id, data: { next_step: v } });
    }
  };

  const scrollToPayments = () => setActiveTab("pagamentos");

  /** Single dispatcher for the stepper CTA and the header primary button. */
  const handleFlowAction = (action: FlowAction) => {
    switch (action) {
      case "go-proposta":
        return setActiveTab("proposta");
      case "send-proposal": {
        const target =
          (leadBudgets as Budget[]).find(
            (b) => b.id === activeBudget?.id && !SIGNED_BUDGET.has(b.status),
          ) ??
          (leadBudgets as Budget[]).find((b) => !SIGNED_BUDGET.has(b.status)) ??
          null;
        if (target && isProposalDocumentReady(target)) setSignatureBudget(target);
        else {
          if (target)
            toast.error("Preencha o documento da proposta (texto ou PDF) antes de enviar.");
          setActiveTab("proposta");
        }
        return;
      }
      case "go-contrato":
      case "send-contract":
        return setActiveTab("contrato");
      case "go-payments":
        return scrollToPayments();
      case "go-entrega":
        return setActiveTab("entrega");
      case "mark-delivered":
        setActiveTab("entrega");
        return setDeliverOpen(true);
    }
  };

  const handleReopen = () => {
    if (!id) return;
    updateLead.mutate({ id, data: { status: "new", lost_reason: null, lost_at: null } });
  };

  const handleMarkDelivered = async () => {
    if (!id) return;
    const pending = linkedProjects.filter((p) => p.phase !== "done");
    await Promise.all(
      pending.map((p) =>
        moveProject.mutateAsync({ projectId: p.id, newPhase: "done", newOrder: 0 }),
      ),
    );
    updateLead.mutate({
      id,
      data: { status: "delivered", delivered_at: new Date().toISOString() },
    });
    setDeliverOpen(false);
  };

  const openNewProject = (phase: ProjectPhase = "backlog") => {
    setEditingProject(null);
    setProjectDialogPhase(phase);
    setProjectDialogOpen(true);
  };

  const openEditProject = (project: ClientProject) => {
    setEditingProject(project);
    setProjectDialogPhase(project.phase);
    setProjectDialogOpen(true);
  };

  const handleMoveProject = (project: ClientProject, direction: "forward" | "backward") => {
    const idx = phases.indexOf(project.phase);
    const next = direction === "forward" ? phases[idx + 1] : phases[idx - 1];
    if (!next) return;
    moveProject.mutate({ projectId: project.id, newPhase: next, newOrder: 0 });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newPhase = result.destination.droppableId as ProjectPhase;
    const project = linkedProjects.find((p) => p.id === result.draggableId);
    if (!project || project.phase === newPhase) return;
    moveProject.mutate({
      projectId: result.draggableId,
      newPhase,
      newOrder: result.destination.index,
    });
  };

  // ── loading / not found ──────────────────────────────────────────────────

  if (leadLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Lead não encontrado.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/admin/pipeline">Voltar para Pipeline</Link>
        </Button>
      </div>
    );
  }

  const primaryCta = !isLost && flow.current?.cta ? flow.current.cta : null;
  const pendingInstallments = (paymentsPlan?.installments ?? []).filter(
    (i) => i.status !== "paid",
  ).length;

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto flex max-w-[1220px] flex-col gap-5">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <Button
            asChild
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-[10px] text-muted-foreground"
          >
            <Link to="/admin/pipeline" aria-label="Voltar">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-display text-[25px] font-bold leading-none tracking-[-0.03em]">
                {lead.name}
              </h1>
              <StatusBadge tone={stageMeta.tone} dot>
                {stageMeta.label}
              </StatusBadge>
            </div>
            <p className="mt-[5px] text-[13px] text-muted-foreground">
              {[lead.email, lead.phone].filter(Boolean).join(" · ") || "Sem contato"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {lead.phone && (
            <Button
              variant="outline"
              className={HEADER_BTN}
              onClick={() => setWhatsappDialogOpen(true)}
            >
              WhatsApp
            </Button>
          )}
          <Button variant="outline" className={HEADER_BTN} onClick={() => setEmailDialogOpen(true)}>
            E-mail
          </Button>
          <Button
            variant="outline"
            className={HEADER_BTN}
            disabled={!lead.client_id}
            title={lead.client_id ? undefined : "Este lead ainda não está vinculado a um cliente"}
            onClick={() => lead.client_id && openClient(lead.client_id)}
          >
            Ficha do cliente
          </Button>
          {primaryCta && (
            <Button
              className={`${HEADER_BTN} px-4`}
              onClick={() => handleFlowAction(primaryCta.action)}
            >
              {primaryCta.label}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-[9px]"
                aria-label="Mais ações"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditLeadOpen(true)}>
                <Pencil className="mr-2 h-3.5 w-3.5" /> Editar dados
              </DropdownMenuItem>
              {isLost ? (
                <DropdownMenuItem onClick={handleReopen}>
                  <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reabrir lead
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => setMarkLostOpen(true)}
                  className="text-bad focus:text-bad"
                >
                  <XCircle className="mr-2 h-3.5 w-3.5" /> Marcar como perdido…
                </DropdownMenuItem>
              )}
              {!isDelivered && !isLost && (
                <DropdownMenuItem onClick={() => setDeliverOpen(true)}>
                  <PackageCheck className="mr-2 h-3.5 w-3.5" /> Marcar como entregue…
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Excluir lead…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── Flow stepper ── */}
      <LeadFlowStepper
        flow={flow}
        lostReason={lead.lost_reason}
        onAction={handleFlowAction}
        onReopen={handleReopen}
      />

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
        <TabsList className="h-auto w-fit flex-wrap gap-[3px] rounded-[10px] bg-muted p-[3px]">
          {TAB_KEYS.map((key, i) => (
            <TabsTrigger key={key} value={key} className={SEG_ITEM}>
              {i + 1}. {TAB_LABELS[key]}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-5 grid items-start gap-4 lg:grid-cols-[1.55fr_1fr]">
          <div className="min-w-0 space-y-4">
            {/* ── Proposta ── */}
            <TabsContent value="proposta" className="mt-0 space-y-4">
              {budgetsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Budget selector */}
                  {(leadBudgets as Budget[]).length > 1 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-muted-foreground">Versão:</span>
                      <ToggleGroup
                        type="single"
                        value={activeBudget?.id ?? ""}
                        onValueChange={(v) => {
                          if (v) setActiveBudgetId(v);
                        }}
                        className="flex-wrap justify-start gap-2"
                      >
                        {(leadBudgets as Budget[]).map((b) => {
                          const cfg = statusMeta(BUDGET_STATUS_META, b.status);
                          return (
                            <ToggleGroupItem
                              key={b.id}
                              value={b.id}
                              className="h-auto gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium hover:border-primary data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                            >
                              <span
                                className={`h-1.5 w-1.5 rounded-full ${TONE_BG_CLASS[cfg.tone]}`}
                              />
                              {b.title} · {cfg.label}
                              {barterBadgeLabel(b.barter_percent ?? 0) && (
                                <span className="ml-1 inline-flex items-center gap-0.5 opacity-80">
                                  <Handshake className="h-3 w-3" />
                                  {barterBadgeLabel(b.barter_percent ?? 0)}
                                </span>
                              )}
                            </ToggleGroupItem>
                          );
                        })}
                      </ToggleGroup>
                    </div>
                  )}

                  {activeBudget && <ProposalSummaryCard budget={activeBudget} />}

                  {/* Inline Budget Form */}
                  <InlineBudgetEditor
                    budget={activeBudget}
                    leadId={lead.id}
                    leadName={lead.name}
                    leadEmail={lead.email}
                    leadMessage={lead.message}
                    leadProjectType={lead.project_type}
                    projects={linkedProjects}
                    onSaved={(newId) => setActiveBudgetId(newId)}
                    onDeleted={() => setActiveBudgetId(null)}
                    onSendSignature={(b) => setSignatureBudget(b)}
                    onGoToSignature={() => setActiveTab("contrato")}
                  />
                  {activeBudget && (
                    <ProposalDocumentWorkspace
                      budget={activeBudget}
                      leadName={lead.name}
                      locked={LOCKED_BUDGET.has(activeBudget.status)}
                    />
                  )}
                  {/* Audit trail for the budget's own proposal-signing flow
                  (distinct from the contract's — see ContractSignatureTimeline
                  in the Contrato tab). Renders nothing until a signature
                  request exists for this budget. */}
                  {activeBudget && (
                    <BudgetSignatureTimeline
                      budgetId={activeBudget.id}
                      signedPdfUrl={activeBudget.signed_pdf_url}
                      finalizedAt={activeBudget.finalized_at}
                    />
                  )}
                </div>
              )}
            </TabsContent>

            {/* ── Contrato ──
            Only the contract. The proposal it is bound to (contracts.budget_id)
            shows as a one-line reference with no actions — pricing, document,
            PDF and the proposal's own signature live in the Proposta tab. */}
            <TabsContent value="contrato" className="mt-0 space-y-4">
              {!hasBudget ? (
                <LockedStep
                  message="Crie a proposta antes do contrato — o contrato e as cobranças ficam vinculados a ela."
                  action={{ label: "Ir para Proposta", onClick: () => setActiveTab("proposta") }}
                />
              ) : (
                <div className="space-y-4">
                  {contractBudget && (
                    <LinkedProposalRef
                      budget={contractBudget}
                      onOpen={() => {
                        setActiveBudgetId(contractBudget.id);
                        setActiveTab("proposta");
                      }}
                    />
                  )}
                  <ContractWorkspace lead={lead} activeBudget={contractBudget} />
                </div>
              )}
            </TabsContent>

            {/* ── Pagamentos ──
            The payment plan of the deal's proposal (closed one when it exists,
            else the active draft so the operator can define entrada/parcelas
            before sending — the client sees the plan on /sign/budget). Collection
            (mark paid, MP links) stays locked until the contract or the
            proposal is signed: impossible to record a payment on an unsigned deal. */}
            <TabsContent value="pagamentos" className="mt-0 space-y-4">
              {!hasBudget || !planBudget ? (
                <LockedStep
                  message="Crie a proposta antes de definir a cobrança — o plano de pagamento é calculado sobre os valores dela."
                  action={{ label: "Ir para Proposta", onClick: () => setActiveTab("proposta") }}
                />
              ) : (
                <div className="space-y-4">
                  {paymentsLocked ? (
                    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
                      <Lock className="h-4 w-4 shrink-0" />
                      Você já pode definir o plano; a cobrança (marcar pago, links de pagamento) é
                      liberada assim que o contrato ou a proposta for assinado.
                    </div>
                  ) : !paymentsBudget ? (
                    <div className="flex items-center gap-2.5 rounded-xl border border-warn/20 bg-warn-soft px-4 py-3 text-sm text-warn">
                      <Lock className="h-4 w-4 shrink-0" />O contrato foi assinado, mas não está
                      vinculado a nenhum orçamento. Crie a proposta com os valores e envie-a para
                      assinatura para liberar a cobrança.
                    </div>
                  ) : null}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <CardTitle className="text-base">{planBudget.title}</CardTitle>
                        <div className="flex items-center gap-2">
                          <StatusBadge
                            tone={statusMeta(BUDGET_STATUS_META, planBudget.status).tone}
                          >
                            {statusMeta(BUDGET_STATUS_META, planBudget.status).label}
                          </StatusBadge>
                          <span className="text-sm font-semibold text-ok">
                            {fmt(planBudget.net_total)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {planBudget.client_project?.title ?? "Sem projeto"} ·{" "}
                        {budgetLineSummary(planBudget.items)}
                        {planSplit.barterTotal > 0 && (
                          <>
                            {" "}
                            · a cobrar{" "}
                            <span className="font-semibold text-foreground">
                              {fmt(planSplit.cashTotal)}
                            </span>
                          </>
                        )}
                      </p>
                    </CardHeader>
                    <CardContent>
                      {planSplit.barterTotal > 0 && (
                        <div className="mb-4 flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
                          <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <div className="space-y-0.5">
                            <p className="font-semibold">
                              {planSplit.isFull
                                ? "Permuta integral"
                                : `Permuta ${planSplit.barterPercent}%`}{" "}
                              · {fmt(planSplit.barterTotal)} em serviços
                            </p>
                            {planBudget.barter_description && (
                              <p className="whitespace-pre-line text-muted-foreground">
                                {planBudget.barter_description}
                              </p>
                            )}
                            {planSplit.isFull && (
                              <p className="text-muted-foreground">
                                Nada a cobrar — nenhuma parcela é gerada para este acordo.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {(planSplit.cashTotal > 0 || (planData?.installments?.length ?? 0) > 0) && (
                        <BudgetPaymentPlan
                          budgetId={planBudget.id}
                          total={planSplit.cashTotal}
                          clientId={lead.client_id ?? null}
                          leadPhone={lead.phone}
                          leadName={lead.name}
                          collectLocked={paymentsLocked}
                        />
                      )}
                      {!paymentsLocked && recurringBilling(planBudget).billed > 0 && (
                        <div className="mt-4 border-t pt-4">
                          <BudgetSubscriptionCard
                            budgetId={planBudget.id}
                            amount={planBudget.recurring_monthly_total}
                            leadPhone={lead.phone}
                            leadName={lead.name}
                          />
                        </div>
                      )}
                      {recurringBilling(planBudget).inBarter && (
                        <p
                          className="mt-4 flex items-start gap-2 border-t pt-4 text-sm text-muted-foreground"
                          data-testid="payments-recurring-in-barter"
                        >
                          <Handshake className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          Mensalidade de {fmt(planBudget.recurring_monthly_total)}/mês incluída na
                          permuta — sem cobrança recorrente.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* ── Entrega ──
            Kanban of the lead's linked ClientProjects across 4 project-level
            phases and — when a project is opened via "Gerenciar Projeto" —
            the full Jira-like ProjectWorkspace (Backlog | Board | Sprints |
            Épicos). Closing the lead ("Marcar como entregue") lives here. */}
            <TabsContent value="entrega" className="mt-0 space-y-4">
              {workspaceProject ? (
                <ProjectWorkspace
                  project={
                    linkedProjects.find((p) => p.id === workspaceProject.id) ?? workspaceProject
                  }
                  onBack={() => setWorkspaceProject(null)}
                />
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[13px] text-muted-foreground">
                      {linkedProjects.length === 0
                        ? "Nenhum projeto vinculado ainda. Ele é criado automaticamente junto com a proposta, ou crie um manualmente."
                        : "Acompanhe a execução. Ao finalizar, marque a entrega para encerrar o lead."}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openNewProject()}>
                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Novo projeto
                      </Button>
                      {!isDelivered && !isLost && (
                        <Button
                          size="sm"
                          onClick={() => setDeliverOpen(true)}
                          disabled={linkedProjects.length === 0}
                        >
                          <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Marcar como entregue
                        </Button>
                      )}
                    </div>
                  </div>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {phases.map((phase) => {
                        const cfg = PHASE_META[phase];
                        const phasePjs = projectsByPhase[phase];
                        return (
                          <div key={phase} className="min-h-[160px] rounded-lg bg-muted/40 p-3">
                            <div className="mb-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-2.5 w-2.5 rounded-full ${TONE_BG_CLASS[cfg.tone]}`}
                                />
                                <span className="text-sm font-semibold">{cfg.label}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant="secondary" className="text-xs">
                                  {phasePjs.length}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => openNewProject(phase)}
                                  title={`Novo projeto em ${cfg.label}`}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <Droppable droppableId={phase}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`min-h-[60px] space-y-2 rounded transition-colors ${
                                    snapshot.isDraggingOver ? "bg-primary/5" : ""
                                  }`}
                                >
                                  {phasePjs.map((project, index) => (
                                    <Draggable
                                      key={project.id}
                                      draggableId={project.id}
                                      index={index}
                                    >
                                      {(drag, dragSnapshot) => (
                                        <div
                                          ref={drag.innerRef}
                                          {...drag.draggableProps}
                                          className={
                                            dragSnapshot.isDragging ? "opacity-80 shadow-lg" : ""
                                          }
                                        >
                                          <ProjectCard
                                            project={project}
                                            phases={phases}
                                            dragHandleProps={drag.dragHandleProps}
                                            budgetStatus={projectBudgetStatus[project.id]}
                                            onEdit={() => openEditProject(project)}
                                            onDelete={() => setProjectToDelete(project)}
                                            onMove={(dir) => handleMoveProject(project, dir)}
                                            onOpenWorkspace={() => setWorkspaceProject(project)}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                  {phasePjs.length === 0 && (
                                    <Button
                                      variant="outline"
                                      className="h-auto w-full border-dashed p-3 text-xs font-normal text-muted-foreground shadow-none hover:border-primary hover:bg-transparent hover:text-primary"
                                      onClick={() => openNewProject(phase)}
                                    >
                                      + Adicionar projeto
                                    </Button>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        );
                      })}
                    </div>
                  </DragDropContext>

                  {isDelivered && (
                    <TestimonialRequestCard
                      lead={lead}
                      clientProjectId={linkedProjects[0]?.id ?? null}
                    />
                  )}
                </>
              )}
            </TabsContent>
          </div>

          {/* ── Sidebar (persistent across tabs) ── */}
          <div className="space-y-4">
            <ProspectOriginCard leadId={lead.id} />
            <Card className="rounded-2xl shadow-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-base">Dados do lead</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setEditLeadOpen(true)}
                    aria-label="Editar dados do lead"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  {[
                    {
                      label: "Origem",
                      value: leadSourceLabel(lead.source),
                    },
                    { label: "Serviço", value: projectTypeLabel(lead.project_type) },
                    { label: "Responsável", value: ownerName },
                    {
                      label: "Criado em",
                      value: format(new Date(lead.created_at), "dd/MM/yyyy", { locale: ptBR }),
                    },
                    ...(lead.phone ? [{ label: "Telefone", value: lead.phone }] : []),
                  ].map((f) => (
                    <div
                      key={f.label}
                      className="flex items-center justify-between gap-3 border-b border-border py-[9px] text-[12.5px]"
                    >
                      <span className="text-muted-foreground">{f.label}</span>
                      <span className="truncate text-right font-semibold">{f.value}</span>
                    </div>
                  ))}
                </div>

                {/* Stage is derived from the documents (deriveStage) — the old
                  manual Select let "Convertido" here disagree with "Novo" in
                  the funnel. Terminal states are set via the ⋯ menu. */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Estágio no funil
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={stageMeta.tone} dot>
                      {stageMeta.label}
                    </StatusBadge>
                    {isLost && lead.lost_at && (
                      <span className="text-xs text-muted-foreground">
                        em {format(new Date(lead.lost_at), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    )}
                    {isDelivered && lead.delivered_at && (
                      <span className="text-xs text-muted-foreground">
                        em {format(new Date(lead.delivered_at), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    )}
                  </div>
                  {isLost && lead.lost_reason && (
                    <p className="text-xs text-muted-foreground">Motivo: {lead.lost_reason}</p>
                  )}
                </div>

                <div className="space-y-2 pt-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {(lead.tags ?? []).map((tag) => (
                      <Badge key={tag} variant="outline" className="gap-1 pr-1 text-xs">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-destructive"
                          aria-label={`Remover tag ${tag}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Adicionar tag..."
                      className="h-7 text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={handleAddTag}
                      aria-label="Adicionar tag"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-base">Mensagem original</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-[13px] leading-[1.65] text-muted-foreground">
                  &ldquo;{lead.message}&rdquo;
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-base">Próximo passo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={nextStepDraft}
                  onChange={(e) => setNextStepDraft(e.target.value)}
                  onBlur={handleNextStepBlur}
                  placeholder="Ex.: Enviar a proposta para assinatura até sexta — decisor já aprovou verbalmente."
                  rows={3}
                  className="rounded-[11px] border-0 bg-accent p-3.5 text-[12.5px] font-semibold leading-[1.5] text-primary placeholder:font-normal placeholder:text-primary/50 focus-visible:ring-1 focus-visible:ring-primary/40"
                />
                <div className="space-y-1.5">
                  <Label htmlFor="lead-follow-up" className="text-xs text-muted-foreground">
                    Follow-up
                  </Label>
                  <Input
                    id="lead-follow-up"
                    type="date"
                    value={lead.next_follow_up_at ? lead.next_follow_up_at.slice(0, 10) : ""}
                    onChange={(e) => handleFollowUpChange(e.target.value)}
                    className={
                      lead.next_follow_up_at &&
                      new Date(lead.next_follow_up_at) < new Date(new Date().toDateString())
                        ? "border-destructive text-destructive"
                        : ""
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="font-display text-base">Histórico de interações</CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-lg px-3 text-xs font-semibold"
                    onClick={() => {
                      setRegisterOpen((v) => !v);
                      setTimeout(() => noteTextareaRef.current?.focus(), 0);
                    }}
                  >
                    {registerOpen ? "Fechar" : "Registrar"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {registerOpen && (
                  <div className="space-y-2.5 rounded-xl border border-border bg-muted p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {(Object.keys(interactionLabels) as InteractionType[]).map((type) => (
                        <Button
                          key={type}
                          type="button"
                          variant={noteType === type ? "default" : "outline"}
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleQuickInteraction(type)}
                        >
                          {interactionIcons[type]}
                          <span className="ml-1.5">{interactionLabels[type]}</span>
                        </Button>
                      ))}
                    </div>
                    <Textarea
                      ref={noteTextareaRef}
                      placeholder="Descreva a interação..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                      className="bg-card"
                    />
                    <div className="flex justify-end">
                      <Button
                        onClick={handleAddNote}
                        disabled={!newNote.trim() || addInteraction.isPending}
                        size="sm"
                        className="h-8 rounded-lg text-xs font-semibold"
                      >
                        {addInteraction.isPending && (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        )}
                        Adicionar
                      </Button>
                    </div>
                  </div>
                )}

                {interactionsLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !interactions || interactions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Nenhuma interação registrada.
                  </p>
                ) : (
                  <div>
                    {interactions.map((i, idx) => (
                      <div key={i.id} className="flex gap-[13px] pb-[18px] last:pb-0">
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`mt-[5px] h-[9px] w-[9px] shrink-0 rounded-full ${interactionDot[i.type]}`}
                          />
                          {idx < interactions.length - 1 && (
                            <span className="w-px flex-1 bg-border" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-[9px]">
                            <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                              {interactionLabels[i.type]}
                            </span>
                            <span className="text-[11.5px] text-muted-foreground">
                              {formatDistanceToNow(new Date(i.created_at), {
                                addSuffix: true,
                                locale: ptBR,
                              })}
                            </span>
                          </div>
                          <p className="mt-[3px] whitespace-pre-wrap text-[13px] leading-[1.5]">
                            {i.content}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>

      {/* ── Dialogs ── */}
      <EmailComposerDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        lead={{ id: lead.id, name: lead.name, email: lead.email }}
        attachment={
          activeBudget?.signed_pdf_url
            ? {
                name: `PROP-${new Date(activeBudget.created_at).getFullYear()}-${activeBudget.id.slice(0, 4).toUpperCase()}.pdf`,
                url: activeBudget.signed_pdf_url,
              }
            : null
        }
      />

      {lead.phone && (
        <WhatsAppTemplateDialog
          open={whatsappDialogOpen}
          onOpenChange={setWhatsappDialogOpen}
          lead={{ id: lead.id, name: lead.name, phone: lead.phone }}
        />
      )}

      <ClientProjectDialog
        open={projectDialogOpen}
        onOpenChange={setProjectDialogOpen}
        project={editingProject}
        defaultPhase={projectDialogPhase}
        defaultLead={{ id: lead.id, name: lead.name, email: lead.email, message: lead.message }}
      />

      <BudgetSignatureDialog
        open={!!signatureBudget}
        budget={signatureBudget}
        onOpenChange={(v) => {
          if (!v) setSignatureBudget(null);
        }}
      />

      <EditLeadDialog open={editLeadOpen} onOpenChange={setEditLeadOpen} lead={lead} />

      <MarkLostDialog lead={lead} open={markLostOpen} onOpenChange={setMarkLostOpen} />

      {/* Delete lead */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px] p-[26px]">
          <div className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-bad-soft text-bad">
            <Trash2 className="h-5 w-5" />
          </div>
          <AlertDialogHeader className="mt-1 space-y-2 text-left">
            <AlertDialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              Excluir lead &quot;{lead.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              Isso remove também{" "}
              {interactions?.length
                ? `as ${interactions.length} interações registradas`
                : "as interações registradas"}
              {(leadBudgets as Budget[]).length
                ? `, ${(leadBudgets as Budget[]).length} orçamento(s)`
                : ""}
              {linkedProjects.length ? ` e ${linkedProjects.length} projeto(s)` : ""}. Esta ação não
              pode ser desfeita.
              {!isLost &&
                ' Se o cliente apenas desistiu, prefira "Marcar como perdido" para manter o histórico.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-3 gap-[9px]">
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={async () => {
                await deleteLead.mutateAsync(lead.id);
                navigate("/admin/pipeline");
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete project */}
      <AlertDialog
        open={!!projectToDelete}
        onOpenChange={(o) => {
          if (!o) setProjectToDelete(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Excluir projeto &quot;{projectToDelete?.title}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              O backlog, sprints e épicos deste projeto serão removidos. Orçamentos vinculados a ele
              perdem o vínculo. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={() => {
                if (projectToDelete) deleteProject.mutate(projectToDelete.id);
                setProjectToDelete(null);
              }}
            >
              Excluir projeto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark delivered */}
      <AlertDialog open={deliverOpen} onOpenChange={setDeliverOpen}>
        <AlertDialogContent className="max-w-[460px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Marcar &quot;{lead.name}&quot; como entregue?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-1.5 text-[13px] leading-[1.6]">
              <span className="block">
                {linkedProjects.filter((p) => p.phase !== "done").length > 0
                  ? `${linkedProjects.filter((p) => p.phase !== "done").length} projeto(s) serão movidos para "Concluído" e o lead sai do funil para a lista de entregues.`
                  : "O lead sai do funil para a lista de entregues."}
              </span>
              {pendingInstallments > 0 && (
                <span className="block font-medium text-warn">
                  Atenção: ainda há {pendingInstallments} parcela(s) em aberto. Elas continuam
                  visíveis em Financeiro.
                </span>
              )}
              {paymentsLocked && (
                <span className="block font-medium text-warn">
                  Atenção: nenhuma proposta ou contrato foi assinado para este lead.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
              onClick={(e) => {
                e.preventDefault();
                void handleMarkDelivered();
              }}
              disabled={moveProject.isPending || updateLead.isPending}
            >
              Confirmar entrega
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── ProposalSummaryCard ─────────────────────────────────────────────────────
// Compact read-only view of the active budget (prototype "Proposta PROP-XXXX"
// table). The full editor below stays the source of truth.

function ProposalSummaryCard({ budget }: { budget: Budget }) {
  const { data: planData } = usePaymentPlanByBudget(budget.id);
  const ref = `PROP-${new Date(budget.created_at).getFullYear()}-${budget.id.slice(0, 4).toUpperCase()}`;
  const fee = budget.gross_total - budget.net_total;
  const oneTimeItems = budget.items.filter((i) => i.pricing_mode !== "recurring");
  const itemsGross = oneTimeItems.reduce((s, i) => s + i.subtotal, 0);
  const discount = Math.max(itemsGross - budget.gross_total, 0);
  const barter = barterSplit(budget.net_total, budget.barter_percent ?? 0);
  const barterLabel = barterBadgeLabel(budget.barter_percent ?? 0);

  const plan = planData?.plan;
  const installments = planData?.installments ?? [];
  const deposit = installments[0];
  const balanceCount = Math.max(installments.length - 1, 0);
  const balanceTotal = installments.slice(1).reduce((s, i) => s + (i.amount ?? 0), 0);

  return (
    <section className={`${SECTION} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-[22px] pb-3.5 pt-[18px]">
        <h2 className={SECTION_TITLE}>Proposta {ref}</h2>
        <div className="flex items-center gap-2">
          {barterLabel && (
            <span className="inline-flex h-[23px] items-center gap-1 rounded-[7px] border border-border px-2.5 text-[11px] font-bold text-muted-foreground">
              <Handshake className="h-3 w-3" /> {barterLabel}
            </span>
          )}
          <StatusBadge shape="tag" tone={statusMeta(BUDGET_STATUS_META, budget.status).tone}>
            {statusMeta(BUDGET_STATUS_META, budget.status).label}
          </StatusBadge>
        </div>
      </div>
      {budget.items.map((it) => (
        <div
          key={it.id}
          className="flex items-center gap-4 border-b border-border px-[22px] py-3.5 text-[13px]"
        >
          <div className="min-w-0 flex-1 truncate font-semibold">{it.title}</div>
          <div className="w-[70px] text-muted-foreground">{itemQty(it)}</div>
          <div className="hidden w-[100px] text-right text-muted-foreground sm:block">
            {itemUnit(it)}
          </div>
          <div className="w-[110px] text-right font-bold">{fmt(it.subtotal)}</div>
        </div>
      ))}
      {discount > 0 && (
        <div className="flex items-center gap-4 border-b border-border px-[22px] py-3.5 text-[13px]">
          <div className="min-w-0 flex-1 text-muted-foreground">
            Desconto do pacote ({budget.discount_percent}%)
          </div>
          <div className="w-[110px] text-right font-bold text-ok">–{fmt(discount)}</div>
        </div>
      )}
      {fee > 0 && (
        <div className="flex items-center gap-4 border-b border-border px-[22px] py-3.5 text-[13px]">
          <div className="min-w-0 flex-1 text-muted-foreground">Taxa da plataforma</div>
          <div className="w-[110px] text-right font-bold text-bad">–{fmt(fee)}</div>
        </div>
      )}
      {barter.barterTotal > 0 && (
        <div className="space-y-1 border-b border-border px-[22px] py-3.5 text-[13px]">
          <div className="flex items-center gap-4">
            <div className="min-w-0 flex-1 text-muted-foreground">
              Permuta ({barter.barterPercent}%) — liquidado em serviços
            </div>
            <div className="w-[110px] text-right font-bold">–{fmt(barter.barterTotal)}</div>
          </div>
          {budget.barter_description && (
            <p className="whitespace-pre-line text-[12.5px] text-muted-foreground">
              <span className="font-semibold text-foreground">Contrapartida:</span>{" "}
              {budget.barter_description}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-muted px-[22px] py-4">
        <div className="flex gap-[26px]">
          {barter.isFull ? (
            <div>
              <div className="text-[11px] text-muted-foreground">Cobrança</div>
              <div className="text-[13.5px] font-bold">Permuta — sem cobrança</div>
            </div>
          ) : plan?.mode === "deposit_balance" && deposit ? (
            <>
              <div>
                <div className="text-[11px] text-muted-foreground">
                  Entrada {plan.deposit_percent ?? ""}%
                </div>
                <div className="text-[13.5px] font-bold">{fmt(deposit.amount ?? 0)}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Saldo em {balanceCount}x</div>
                <div className="text-[13.5px] font-bold">{fmt(balanceTotal)}</div>
              </div>
            </>
          ) : plan?.mode === "installments" && installments.length > 0 ? (
            <div>
              <div className="text-[11px] text-muted-foreground">
                {installments.length} parcelas
              </div>
              <div className="text-[13.5px] font-bold">{fmt(installments[0].amount ?? 0)} cada</div>
            </div>
          ) : (
            <div>
              <div className="text-[11px] text-muted-foreground">Cobrança</div>
              <div className="text-[13.5px] font-bold">{plan ? "À vista" : "A definir"}</div>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[11px] text-muted-foreground">Total líquido</div>
          <div className="text-[19px] font-bold tracking-[-0.02em]">{fmt(budget.net_total)}</div>
          {barter.barterTotal > 0 && (
            <div className="mt-0.5 text-[12.5px] font-semibold text-muted-foreground">
              A cobrar: {fmt(barter.cashTotal)}
            </div>
          )}
          {budget.recurring_monthly_total > 0 && (
            <div className="mt-0.5 text-[12.5px] font-semibold text-muted-foreground">
              {recurringBilling(budget).inBarter
                ? `Mensalidade ${fmt(budget.recurring_monthly_total)}/mês · incluída na permuta`
                : `+ ${fmt(budget.recurring_monthly_total)}/mês`}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── LinkedProposalRef ───────────────────────────────────────────────────────
// One-line pointer from the Contrato tab to the proposal the contract is bound
// to. Deliberately has no actions: PDF, copy and the proposal's signature
// belong to the Proposta tab (flows/contrato.md R7).

function LinkedProposalRef({ budget, onOpen }: { budget: Budget; onOpen: () => void }) {
  const ref = `PROP-${new Date(budget.created_at).getFullYear()}-${budget.id.slice(0, 4).toUpperCase()}`;
  const meta = statusMeta(BUDGET_STATUS_META, budget.status);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-muted/40 px-4 py-2.5 text-[13px]">
      <span className="text-muted-foreground">Proposta vinculada</span>
      <span className="font-semibold">{ref}</span>
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{budget.title}</span>
      <StatusBadge shape="tag" tone={meta.tone}>
        {meta.label}
      </StatusBadge>
      <span className="font-bold">{fmt(budget.net_total)}</span>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onOpen}>
        Ver proposta <ChevronRight className="ml-1 h-3 w-3" />
      </Button>
    </div>
  );
}

// ─── LockedStep ───────────────────────────────────────────────────────────────

function LockedStep({
  message,
  action,
}: {
  message: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Lock className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="max-w-xs text-sm text-muted-foreground">{message}</p>
      {action && (
        <Button size="sm" variant="outline" onClick={action.onClick}>
          {action.label} <ChevronRight className="ml-1 h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

// ─── InlineBudgetEditor ───────────────────────────────────────────────────────
// A proposal is a package: one or more items (service types or custom lines),
// an optional package discount and the platform fee. The single "Tipo de
// serviço" select became BudgetItemsEditor; totals come from
// calculatePackageTotals so what the operator sees equals what gets saved.

function InlineBudgetEditor({
  budget,
  leadId,
  leadName,
  leadEmail,
  leadMessage,
  leadProjectType,
  projects,
  onSaved,
  onDeleted,
  onSendSignature,
  onGoToSignature,
}: {
  budget: Budget | null;
  leadId: string;
  leadName: string;
  leadEmail: string | null;
  leadMessage: string;
  leadProjectType: string | null;
  projects: ClientProject[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onSendSignature: (budget: Budget) => void;
  onGoToSignature: () => void;
}) {
  const { data: serviceTypes = [], isLoading: serviceTypesLoading } = useServiceTypes();
  const { data: businessSettings } = useBusinessSettings();
  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();
  const deleteBudget = useDeleteBudget();
  const createProject = useCreateClientProject();
  const [confirmDeleteBudget, setConfirmDeleteBudget] = useState(false);

  const fallbackRate = businessSettings?.default_hourly_rate ?? 80;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [items, setItems] = useState<BudgetItemInput[]>([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [platform, setPlatform] = useState("direct");
  const [customFee, setCustomFee] = useState(0);
  const [barterEnabled, setBarterEnabled] = useState(false);
  const [barterPercent, setBarterPercent] = useState(100);
  const [barterDescription, setBarterDescription] = useState("");
  const [barterChargeRecurring, setBarterChargeRecurring] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEditing = !!budget;

  // Reset form when budget changes
  useEffect(() => {
    if (budget) {
      setTitle(budget.title);
      setDescription(budget.description ?? "");
      setProjectId(budget.client_project_id ?? "");
      setItems(
        budget.items.map((i) => ({
          service_type_id: i.service_type_id,
          title: i.title,
          description: i.description,
          pricing_mode: i.pricing_mode,
          hours: i.hours ?? 0,
          hourly_rate: i.hourly_rate ?? 0,
          fixed_price: i.fixed_price ?? 0,
          monthly_amount: i.monthly_amount ?? 0,
        })),
      );
      setDiscountPercent(budget.discount_percent ?? 0);
      setPlatform(budget.platform ?? "direct");
      setCustomFee(
        budget.platform === "other" && budget.platform_fee
          ? Math.round(budget.platform_fee * 100)
          : 0,
      );
      setBarterEnabled((budget.barter_percent ?? 0) > 0);
      setBarterPercent((budget.barter_percent ?? 0) > 0 ? budget.barter_percent : 100);
      setBarterDescription(budget.barter_description ?? "");
      setBarterChargeRecurring(!!budget.barter_charge_recurring);
    } else {
      // New proposal: one item prefilled from the lead's requested service
      // (same name-match heuristic the old auto-creation used). Wait for the
      // service types so this runs once — re-running on load wiped anything
      // the operator had already typed.
      if (serviceTypesLoading) return;
      const st = leadProjectType
        ? serviceTypes.find((s) => s.name.toLowerCase().includes(leadProjectType.toLowerCase()))
        : undefined;
      setTitle(
        st
          ? `${st.name} – ${leadName}`
          : leadProjectType
            ? `${projectTypeLabel(leadProjectType)} – ${leadName}`
            : `Proposta – ${leadName}`,
      );
      setDescription("");
      setProjectId(projects[0]?.id ?? "");
      setItems([
        st
          ? itemFromServiceType(st, fallbackRate)
          : {
              ...blankItem(fallbackRate),
              title: leadProjectType ? projectTypeLabel(leadProjectType) : "",
            },
      ]);
      setDiscountPercent(0);
      setPlatform("direct");
      setCustomFee(0);
      setBarterEnabled(false);
      setBarterPercent(100);
      setBarterDescription("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget?.id, serviceTypesLoading]);

  const totals = calculatePackageTotals({
    items: items.map((i) => ({
      pricingMode: i.pricing_mode,
      hours: i.hours,
      hourlyRate: i.hourly_rate,
      fixedPrice: i.fixed_price,
      monthlyAmount: i.monthly_amount,
    })),
    discountPercent,
    platform,
    customFee,
  });
  const barter = barterSplit(totals.net, barterEnabled ? barterPercent : 0);

  const isSaved = !!budget;
  const locked = isSaved && LOCKED_BUDGET.has(budget.status);
  const isSigned = isSaved && SIGNED_BUDGET.has(budget.status);
  const canSendSignature = isSaved && !isSigned; // draft, sent (resend) or rejected (resend after edits)
  const documentReady = isSaved && budget ? isProposalDocumentReady(budget) : true;
  const showDiscount = items.length > 1 || discountPercent > 0;

  const buildPayload = (targetProjectId: string): CreateBudgetData => ({
    title: title.trim(),
    description: description.trim() || undefined,
    lead_id: leadId,
    client_project_id: targetProjectId,
    platform,
    custom_fee_percent: platform === "other" ? customFee : 0,
    discount_percent: showDiscount ? discountPercent : 0,
    barter_percent: barterEnabled ? barterPercent : 0,
    barter_description: barterEnabled ? barterDescription.trim() || null : null,
    barter_charge_recurring: barterEnabled ? barterChargeRecurring : false,
    items: items.map((i) => ({ ...i, title: i.title.trim() })),
  });

  const validate = (): string | null => {
    if (!title.trim()) return "Preencha o título da proposta";
    if (items.length === 0) return "Adicione pelo menos um serviço ao pacote";
    const bad = items.findIndex((i) => !i.title.trim());
    if (bad >= 0) return `Dê um nome ao item ${bad + 1}`;
    const zero = totals.itemSubtotals.findIndex((v) => v <= 0);
    if (zero >= 0) return `O item ${zero + 1} precisa ter valor maior que zero`;
    if (totals.net <= 0) return "O valor da proposta precisa ser maior que zero";
    if (barterEnabled && (!(barterPercent > 0) || barterPercent > 100))
      return "Informe a porcentagem da permuta (1 a 100)";
    if (barterEnabled && !barterDescription.trim()) return "Descreva a contrapartida da permuta";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    setSaving(true);
    try {
      // The project is created implicitly on the first proposal, so the
      // operator never has to visit "Entrega" before pricing the deal.
      let targetProjectId = projectId;
      if (!targetProjectId) {
        const created = await createProject.mutateAsync({
          title: title.trim(),
          description: leadMessage,
          client_name: leadName,
          client_email: leadEmail,
          lead_id: leadId,
          phase: "backlog",
          priority: 1,
          tags: leadProjectType ? [leadProjectType] : [],
        });
        targetProjectId = created.id;
        setProjectId(created.id);
      }

      const payload = buildPayload(targetProjectId);
      if (isEditing && budget) {
        await updateBudget.mutateAsync({ id: budget.id, data: payload });
        onSaved(budget.id);
      } else {
        const created = await createBudget.mutateAsync(payload);
        onSaved(created.id ?? "");
      }
    } catch {
      // the hooks already toast the error
    } finally {
      setSaving(false);
    }
  };

  /** Duplicates a frozen proposal (items included) as a new draft so numbers can change without touching the signed one. */
  const handleNewVersion = async () => {
    if (!budget) return;
    setSaving(true);
    try {
      // An uploaded PDF (and its fields) is tied to the old version's file — the
      // operator re-uploads it; authored text carries over so it can be adjusted.
      const created = await createBudget.mutateAsync({
        ...buildPayload(budget.client_project_id ?? projectId),
        title: /v\d+$/.test(budget.title)
          ? budget.title.replace(/v(\d+)$/, (_, n) => `v${Number(n) + 1}`)
          : `${budget.title} v2`,
        document_mode: budget.document_mode ?? "auto",
        richtext_content: budget.document_mode === "richtext" ? budget.richtext_content : null,
      });
      toast.success("Nova versão criada como rascunho");
      onSaved(created.id ?? "");
    } catch {
      // hook toasts
    } finally {
      setSaving(false);
    }
  };

  const isPending =
    saving || createBudget.isPending || updateBudget.isPending || createProject.isPending;

  const statusBadgeCfg = budget ? statusMeta(BUDGET_STATUS_META, budget.status) : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {isEditing ? "Editar Proposta" : "Nova Proposta"}
            </CardTitle>
            {statusBadgeCfg && (
              <StatusBadge tone={statusBadgeCfg.tone}>{statusBadgeCfg.label}</StatusBadge>
            )}
          </div>
          {locked && (
            <p className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" />
              {isSigned
                ? "Proposta assinada — os valores estão congelados. Para alterar, crie uma nova versão."
                : "Proposta enviada ao cliente — os valores estão congelados. Para alterar, crie uma nova versão e reenvie."}
            </p>
          )}
          {budget?.status === "rejected" && (
            <p className="flex items-center gap-1.5 pt-1 text-xs text-bad">
              <XCircle className="h-3 w-3" />O cliente recusou esta proposta. Ajuste os valores e
              reenvie, ou crie uma nova versão.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Título + Projeto */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="lead-titulo">Título</Label>
              <Input
                id="lead-titulo"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`Proposta – ${leadName}`}
                disabled={locked}
              />
            </div>
            {projects.length > 1 ? (
              <div className="space-y-1.5">
                <Label htmlFor="lead-projeto">Projeto</Label>
                <Select value={projectId} onValueChange={setProjectId} disabled={locked}>
                  <SelectTrigger id="lead-projeto">
                    <SelectValue placeholder="Selecionar projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <span className="text-sm font-medium leading-none">Projeto</span>
                <p className="flex h-10 items-center text-sm text-muted-foreground">
                  {projects[0]?.title ?? "Será criado automaticamente ao salvar"}
                </p>
              </div>
            )}
          </div>

          {/* Descrição */}
          <div className="space-y-1.5">
            <Label htmlFor="lead-escopo-descricao">Escopo / Descrição</Label>
            <Textarea
              id="lead-escopo-descricao"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o escopo do serviço..."
              rows={3}
              disabled={locked}
            />
          </div>

          <Separator />

          {/* Itens do pacote */}
          <BudgetItemsEditor
            items={items}
            onChange={setItems}
            serviceTypes={serviceTypes}
            fallbackRate={fallbackRate}
            disabled={locked}
          />

          <Separator />

          {/* Plataforma + desconto */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="lead-plataforma">Plataforma</Label>
              <Select value={platform} onValueChange={setPlatform} disabled={locked}>
                <SelectTrigger id="lead-plataforma">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direto (0%)</SelectItem>
                  <SelectItem value="workana">Workana (20%)</SelectItem>
                  <SelectItem value="other">Outra (custom)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {platform === "other" && (
              <div className="space-y-1.5">
                <Label htmlFor="lead-taxa">Taxa (%)</Label>
                <Input
                  id="lead-taxa"
                  type="number"
                  min={0}
                  max={50}
                  value={customFee}
                  onChange={(e) => setCustomFee(Number(e.target.value))}
                  disabled={locked}
                />
              </div>
            )}
            {showDiscount && (
              <div className="space-y-1.5">
                <Label htmlFor="lead-desconto-do-pacote">Desconto do pacote (%)</Label>
                <Input
                  id="lead-desconto-do-pacote"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={discountPercent}
                  onChange={(e) =>
                    setDiscountPercent(Math.min(100, Math.max(0, Number(e.target.value))))
                  }
                  disabled={locked}
                  aria-label="Desconto do pacote em porcentagem"
                />
              </div>
            )}
          </div>

          {/* Permuta — part (or all) of the net value settled in services */}
          <div
            className="space-y-3 rounded-lg border border-border p-4"
            data-testid="budget-barter"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Handshake className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="barter-switch" className="cursor-pointer">
                  Parte do valor em permuta
                </Label>
              </div>
              <Switch
                id="barter-switch"
                checked={barterEnabled}
                onCheckedChange={(v) => {
                  setBarterEnabled(v);
                  if (v && !(barterPercent > 0)) setBarterPercent(100);
                }}
                disabled={locked}
                aria-label="Acordo de permuta"
              />
            </div>
            {barterEnabled && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-em-permuta">% em permuta</Label>
                  <Input
                    id="lead-em-permuta"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    value={barterPercent}
                    onChange={(e) =>
                      setBarterPercent(Math.min(100, Math.max(0, Number(e.target.value))))
                    }
                    disabled={locked}
                    aria-label="Porcentagem do valor em permuta"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    100% = permuta integral, sem cobrança.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-contrapartida-o-que-voce-rec">
                    Contrapartida (o que você recebe em troca)
                  </Label>
                  <Textarea
                    id="lead-contrapartida-o-que-voce-rec"
                    rows={2}
                    value={barterDescription}
                    onChange={(e) => setBarterDescription(e.target.value)}
                    placeholder="Ex.: 12 sessões de nutrição, 6 meses de academia…"
                    disabled={locked}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Aparece na proposta e no contrato. A taxa da plataforma continua sobre o valor
                    total.
                  </p>
                </div>
              </div>
            )}
            {barterEnabled && totals.recurringMonthly > 0 && (
              <div
                className="flex items-start justify-between gap-3 border-t border-border pt-3"
                data-testid="budget-barter-recurring"
              >
                <div className="space-y-0.5">
                  <Label htmlFor="barter-charge-recurring" className="cursor-pointer">
                    Cobrar a mensalidade mesmo assim
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    {barterChargeRecurring
                      ? `A mensalidade de ${fmt(totals.recurringMonthly)}/mês será cobrada normalmente (assinatura mensal).`
                      : `Desligado: a mensalidade de ${fmt(totals.recurringMonthly)}/mês entra na permuta e não é cobrada.`}
                  </p>
                </div>
                <Switch
                  id="barter-charge-recurring"
                  checked={barterChargeRecurring}
                  onCheckedChange={setBarterChargeRecurring}
                  disabled={locked}
                  aria-label="Cobrar mensalidade na permuta"
                />
              </div>
            )}
          </div>

          {/* Resumo financeiro */}
          <div
            className="space-y-2 rounded-lg bg-muted/40 p-4 text-sm"
            data-testid="budget-summary"
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Resumo Financeiro
            </p>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {items.length > 1 ? `Subtotal (${items.length} serviços)` : "Bruto"}
              </span>
              <span className="font-medium">{fmt(totals.itemsGross)}</span>
            </div>
            {totals.discount > 0 && (
              <div className="flex justify-between text-ok">
                <span>Desconto ({discountPercent}%)</span>
                <span>– {fmt(totals.discount)}</span>
              </div>
            )}
            {totals.fee > 0 && (
              <div className="flex justify-between text-destructive">
                <span>Taxa ({(totals.feeRate * 100).toFixed(0)}%)</span>
                <span>– {fmt(totals.fee)}</span>
              </div>
            )}
            {totals.totalHours > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Prazo estimado ({totals.totalHours}h)</span>
                <span>{estimateBusinessDays(totals.totalHours)} dias úteis</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2 text-base font-bold">
              <span>Líquido</span>
              <span className="text-ok" data-testid="budget-net">
                {fmt(totals.net)}
              </span>
            </div>
            {barter.barterTotal > 0 && (
              <>
                <div className="flex justify-between text-muted-foreground">
                  <span>Permuta ({barter.barterPercent}%) — em serviços</span>
                  <span>– {fmt(barter.barterTotal)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>A cobrar</span>
                  <span data-testid="budget-cash">
                    {fmt(barter.cashTotal)}
                    {barter.isFull && (
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        · permuta integral
                      </span>
                    )}
                  </span>
                </div>
              </>
            )}
            {totals.recurringMonthly > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Mensalidade</span>
                <span data-testid="budget-recurring">
                  {barterEnabled && !barterChargeRecurring
                    ? `${fmt(totals.recurringMonthly)}/mês · incluída na permuta`
                    : `+ ${fmt(totals.recurringMonthly)}/mês`}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bottom actions */}
      <div className="flex flex-col gap-2 border-t pt-2 sm:flex-row sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {isSaved && budget && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard
                    .writeText(buildProposalText(budget, leadName))
                    .then(() => toast.success("Proposta copiada!"));
                }}
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar
              </Button>
              {locked && (
                <Button variant="outline" size="sm" onClick={handleNewVersion} disabled={isPending}>
                  <Files className="mr-1.5 h-3.5 w-3.5" /> Nova versão
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-bad hover:text-bad"
                onClick={() => setConfirmDeleteBudget(true)}
                disabled={isPending}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Excluir
              </Button>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {!locked && (
            <Button
              variant={isSaved ? "outline" : "default"}
              onClick={handleSave}
              disabled={isPending}
            >
              {isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : !isSaved ? (
                <Plus className="mr-1.5 h-4 w-4" />
              ) : null}
              {isEditing ? "Salvar Alterações" : "Criar Proposta"}
            </Button>
          )}
          {canSendSignature && budget && (
            <Button
              onClick={() => onSendSignature(budget)}
              disabled={isPending || !documentReady}
              title={
                documentReady
                  ? undefined
                  : "Preencha o documento da proposta (texto ou PDF) abaixo antes de enviar"
              }
            >
              <Send className="mr-1.5 h-4 w-4" />
              {budget.status === "draft" ? "Enviar para Assinatura" : "Reenviar para Assinatura"}
            </Button>
          )}
          {isSigned && (
            <Button onClick={onGoToSignature}>
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
              Ir para Contrato
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDeleteBudget} onOpenChange={setConfirmDeleteBudget}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Excluir orçamento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {isSigned
                ? `"${budget?.title}" já está assinado/aceito — excluir remove o registro definitivamente, junto com itens, plano de pagamento e parcelas. Um contrato vinculado, se houver, apenas perde a referência ao orçamento e não é apagado. Esta ação não pode ser desfeita.`
                : `"${budget?.title}" será removido junto com itens, plano de pagamento e parcelas. Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={() => {
                if (!budget) return;
                deleteBudget.mutate(budget.id, { onSuccess: () => onDeleted() });
                setConfirmDeleteBudget(false);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── ProjectCard ──────────────────────────────────────────────────────────────

function ProjectCard({
  project,
  phases,
  dragHandleProps,
  budgetStatus,
  onEdit,
  onDelete,
  onMove,
  onOpenWorkspace,
}: {
  project: ClientProject;
  phases: ProjectPhase[];
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  budgetStatus?: string;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dir: "forward" | "backward") => void;
  onOpenWorkspace: () => void;
}) {
  const phaseIdx = phases.indexOf(project.phase);
  const priorityLabels = ["Baixa", "Normal", "Alta", "Urgente"];
  const budgetBadge = projectBudgetMeta(budgetStatus);

  return (
    <Card className="border shadow-none">
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-1">
          <div className="flex min-w-0 flex-1 items-start gap-1.5">
            <div
              {...dragHandleProps}
              className="mt-0.5 shrink-0 cursor-grab text-muted-foreground/40 hover:text-muted-foreground"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </div>
            <p className="line-clamp-2 text-sm font-medium leading-snug">{project.title}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label="Ações do projeto"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Excluir…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {project.description && (
          <p className="line-clamp-1 text-xs text-muted-foreground">{project.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-1.5">
          {project.priority > 0 && (
            <span
              className={`text-xs font-medium ${TONE_TEXT_CLASS[PRIORITY_TONE[project.priority] ?? "neutral"]}`}
            >
              {priorityLabels[project.priority]}
            </span>
          )}
          {project.due_date && (
            <span className="text-xs text-muted-foreground">
              {format(new Date(project.due_date), "dd/MM/yy")}
            </span>
          )}
        </div>

        <StatusBadge shape="tag" tone={budgetBadge.tone}>
          {budgetBadge.label}
        </StatusBadge>

        {project.uses_jira_mode && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full gap-1.5 text-xs"
            onClick={onOpenWorkspace}
          >
            <LayoutGrid className="h-3 w-3" /> Gerenciar Projeto
          </Button>
        )}

        <div className="flex gap-1 pt-1">
          {phaseIdx > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 flex-1 gap-1 text-xs"
              onClick={() => onMove("backward")}
            >
              <ArrowLeft className="h-3 w-3" /> Voltar
            </Button>
          )}
          {phaseIdx < phases.length - 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 flex-1 gap-1 text-xs"
              onClick={() => onMove("forward")}
            >
              Avançar <ArrowRight className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
